/* eslint-disable @nx/enforce-module-boundaries -- deep `libs/xplat/api/dto` paths avoid the dto barrel -> tool-tracker -> xplat-api-services cycle */
import type { ForemanEmployeeInOut } from '../../../../dto/src/lib/models/foreman/foreman-employee-in-out.model';

/**
 * Dependency-free half of the in-out helpers. Kept separate from
 * `foreman-employee-in-out.utils` (which needs `@ups/xplat/utils` for GUIDs)
 * so pure consumers such as the draft-state fingerprint do not drag Angular
 * providers into platform-agnostic code and unit tests.
 */

const GUID_REGEX =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

export function isValidForemanGuid(value: string | null | undefined): boolean {
  return !!value && value !== EMPTY_GUID && GUID_REGEX.test(value);
}

/**
 * In-outs that can be stored on labor and sent to the submit API.
 * Excludes empty time-segment rows (no entity yet) and partial objects produced
 * from spreading undefined entries (missing foremanEmployeeInOutID → empty GUID on save).
 */
export function isPersistableForemanEmployeeInOut(
  e: ForemanEmployeeInOut | null | undefined
): e is ForemanEmployeeInOut {
  if (!e) return false;
  if (!isValidForemanGuid(e.foremanEmployeeInOutID)) return false;
  if (!e.timeIn || !e.timeOut) return false;
  return true;
}

/**
 * True when the persistable time-segment set differs (id / times / active).
 * Used so an in/out rearrangement with unchanged hours still marks the labor
 * dirty — otherwise #30217's remote labor overlay can revert the draft on a
 * job-list refresh before submit (submit already sends hours>0 labors).
 */
export function havePersistableInOutsChanged(
  previous: ForemanEmployeeInOut[] | null | undefined,
  next: ForemanEmployeeInOut[] | null | undefined
): boolean {
  const prevKeys = persistableInOutIdentityKeys(previous);
  const nextKeys = persistableInOutIdentityKeys(next);
  if (prevKeys.length !== nextKeys.length) {
    return true;
  }
  for (let i = 0; i < prevKeys.length; i++) {
    if (prevKeys[i] !== nextKeys[i]) {
      return true;
    }
  }
  return false;
}

/**
 * Stable fingerprint of the persistable time-segment set, order-independent.
 * Lets a labor's committed state be compared without keeping a full copy.
 */
export function getPersistableInOutsSignature(
  inOuts: ForemanEmployeeInOut[] | null | undefined
): string {
  return persistableInOutIdentityKeys(inOuts).join(';');
}

function persistableInOutIdentityKeys(
  inOuts: ForemanEmployeeInOut[] | null | undefined
): string[] {
  return (inOuts ?? [])
    .filter(isPersistableForemanEmployeeInOut)
    .map((e) => {
      const timeIn = e.timeIn instanceof Date ? e.timeIn.getTime() : e.timeIn;
      const timeOut =
        e.timeOut instanceof Date ? e.timeOut.getTime() : e.timeOut;
      return `${e.foremanEmployeeInOutID}|${timeIn}|${timeOut}|${e.bActive === true}`;
    })
    .sort();
}
